/**
 * GDPR erasure (Art. 17) and subject access (Art. 15) contracts (TO-232,
 * ADR-0057). compliance-service owns the requests; the five participant
 * contexts act on `erasure.step_requested` / `sar.requested` and report back.
 */

/** Lifecycle of an erasure request (gdpr-erasure REQ-002..009). */
export enum ErasureStatus {
	PREVIEW_PENDING = 'PREVIEW_PENDING',
	AWAITING_CONFIRMATION = 'AWAITING_CONFIRMATION',
	GRACE_PERIOD = 'GRACE_PERIOD',
	ON_HOLD = 'ON_HOLD',
	ERASING = 'ERASING',
	COMPLETED = 'COMPLETED',
	CANCELLED = 'CANCELLED',
}

/** The statuses in which a request is still open: at most one per `(tenant, customer)`. */
export const OPEN_ERASURE_STATUSES: readonly ErasureStatus[] = [
	ErasureStatus.PREVIEW_PENDING,
	ErasureStatus.AWAITING_CONFIRMATION,
	ErasureStatus.GRACE_PERIOD,
	ErasureStatus.ON_HOLD,
	ErasureStatus.ERASING,
]

/** Why the subject is erased. `RETENTION_EXPIRED` is chosen by hand until Epic 04 Story 10 ships. */
export enum ErasureBasis {
	CUSTOMER_REQUEST = 'CUSTOMER_REQUEST',
	RETENTION_EXPIRED = 'RETENTION_EXPIRED',
}

/** The step each participant is asked to run. */
export enum ErasureStep {
	INVENTORY = 'INVENTORY',
	SOFT_DELETE = 'SOFT_DELETE',
	ERASE = 'ERASE',
}

/** What the final erasure does to a category of records. */
export enum ErasureAction {
	ANONYMIZE = 'ANONYMIZE',
	DELETE = 'DELETE',
	REDACT = 'REDACT',
	UNLINK = 'UNLINK',
	RETAIN = 'RETAIN',
}

/** A bounded context that holds subject data. */
export enum ComplianceContext {
	CUSTOMER = 'CUSTOMER',
	OFFER = 'OFFER',
	TRACKING = 'TRACKING',
	NOTIFICATION = 'NOTIFICATION',
	AUDIT = 'AUDIT',
}

/** Every context, in the fixed order used by previews, bundles and certificates. */
export const COMPLIANCE_CONTEXTS: readonly ComplianceContext[] = [
	ComplianceContext.CUSTOMER,
	ComplianceContext.OFFER,
	ComplianceContext.TRACKING,
	ComplianceContext.NOTIFICATION,
	ComplianceContext.AUDIT,
]

/** The contexts that MUST report a step before the request advances (gdpr-erasure Contract). */
export const EXPECTED_CONTEXTS: Readonly<Record<ErasureStep, readonly ComplianceContext[]>> = {
	[ErasureStep.INVENTORY]: COMPLIANCE_CONTEXTS,
	[ErasureStep.SOFT_DELETE]: [ComplianceContext.CUSTOMER, ComplianceContext.OFFER],
	[ErasureStep.ERASE]: [
		ComplianceContext.CUSTOMER,
		ComplianceContext.OFFER,
		ComplianceContext.TRACKING,
		ComplianceContext.NOTIFICATION,
	],
}

/** An inventory count category (gdpr-erasure REQ-003). */
export enum ErasureCategory {
	CUSTOMERS = 'customers',
	CONTACT_POINTS = 'contactPoints',
	NOTES = 'notes',
	ATTRIBUTES = 'attributes',
	CONSENTS = 'consents',
	OFFERS = 'offers',
	TRACKING_EVENTS = 'trackingEvents',
	NOTIFICATIONS = 'notifications',
	AUDIT_EVENTS = 'auditEvents',
}

/** The fixed action per category, in preview order (gdpr-erasure REQ-003). */
export const ERASURE_CATEGORY_ACTIONS: Readonly<Record<ErasureCategory, ErasureAction>> = {
	[ErasureCategory.CUSTOMERS]: ErasureAction.ANONYMIZE,
	[ErasureCategory.CONTACT_POINTS]: ErasureAction.ANONYMIZE,
	[ErasureCategory.NOTES]: ErasureAction.ANONYMIZE,
	[ErasureCategory.ATTRIBUTES]: ErasureAction.DELETE,
	[ErasureCategory.CONSENTS]: ErasureAction.RETAIN,
	[ErasureCategory.OFFERS]: ErasureAction.REDACT,
	[ErasureCategory.TRACKING_EVENTS]: ErasureAction.UNLINK,
	[ErasureCategory.NOTIFICATIONS]: ErasureAction.REDACT,
	[ErasureCategory.AUDIT_EVENTS]: ErasureAction.RETAIN,
}

/** Lifecycle of a subject access request (gdpr-subject-access). */
export enum SarStatus {
	COLLECTING = 'COLLECTING',
	READY = 'READY',
	EXPIRED = 'EXPIRED',
}

/** How the officer identifies the subject of a SAR. The value itself is never stored. */
export enum SarIdentifierType {
	EMAIL = 'EMAIL',
	PHONE = 'PHONE',
	CUSTOMER_ID = 'CUSTOMER_ID',
	EXTERNAL_ID = 'EXTERNAL_ID',
}

/** A SAR bundle format. */
export enum SarFormat {
	JSON = 'JSON',
	PDF = 'PDF',
}

/** The subject manifest participants read by key (ADR-0057 §3). Never sent in an event. */
export interface ErasureManifest {
	requestId: string
	tenantId: string
	customerId: string
	/** Stored and normalised values of every contact point, soft-deleted ones included. */
	contactValues: string[]
	/** Every offer of the customer, soft-deleted ones included. */
	offerIds: string[]
}

/** The SAR variant of the subject manifest. */
export interface SarManifest {
	sarId: string
	tenantId: string
	customerId: string
	contactValues: string[]
	offerIds: string[]
}

/** One ledger row of an erasure request, as served. */
export interface ErasureStepDTO {
	context: ComplianceContext
	step: ErasureStep
	reportedAt: string
	counts: Record<string, number>
}

/** One preview row (gdpr-erasure REQ-003). */
export interface ErasurePreviewItem {
	context: ComplianceContext
	category: ErasureCategory
	count: number
	action: ErasureAction
}

/** `ErasureRequest` as served by `/compliance/erasures`. */
export interface ErasureRequestDTO {
	id: string
	customerId: string
	status: ErasureStatus
	basis: ErasureBasis
	note: string | null
	graceDays: number
	requestedBy: string
	requestedAt: string
	confirmedBy: string | null
	confirmedAt: string | null
	graceEndsAt: string | null
	cancelledAt: string | null
	completedAt: string | null
	certificateSha256: string | null
	currentStep: ErasureStep | null
	steps: ErasureStepDTO[]
	preview: ErasurePreviewItem[]
}

/** `LegalHold` as served by `/compliance/legal-holds`. */
export interface LegalHoldDTO {
	id: string
	customerId: string
	reason: string
	active: boolean
	placedBy: string
	placedAt: string
	releasedBy: string | null
	releasedAt: string | null
}

/** One reported SAR section, as served. */
export interface SarSectionDTO {
	context: ComplianceContext
	recordCount: number
	reportedAt: string
}

/** `SubjectAccessRequest` as served by `/compliance/sars`. */
export interface SubjectAccessRequestDTO {
	id: string
	customerId: string
	identifierType: SarIdentifierType
	status: SarStatus
	requestedBy: string
	requestedAt: string
	dueBy: string
	readyAt: string | null
	expiresAt: string | null
	sections: SarSectionDTO[]
}

/** A presigned download (certificate or SAR bundle). */
export interface ComplianceDownloadLink {
	url: string
	expiresAt: string
}

/** A page of compliance items. */
export interface CompliancePage<T> {
	data: T[]
	total: number
	page: number
	limit: number
	totalPages: number
}

/**
 * The cap on a `CustomerSubjectPort` identifier match (`@today/service-ports`):
 * one more than the 10 the caller shows, so it can tell "more than 10".
 */
export const CUSTOMER_SUBJECT_MATCH_LIMIT = 11
